import { Button, Stack, Typography } from '@mui/material';
import { CustomMetaTitle, Loader } from 'Components';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { Step2 } from './Step2';
import { Step1 } from './Step1';
import { SuccessDialog } from './SuccessDialog';
import { format } from 'date-fns';
import { RequestsContext } from 'Context';
import { createRequest, getRequestFields, IFormMetaData } from 'API';
import { files, getCleanFileName } from 'Utils';
import { useNavigate, useParams } from 'react-router-dom';
import { REQUESTS_FORM_ID } from 'Enum/RequestsFormId';
import { DynamicArrow } from 'Components/DynamicArrow';

interface IUploadFilesMain {
  nextStep: () => void;
  prevStep: () => void;
}

export const UploadFilesMain = ({ nextStep, prevStep }: IUploadFilesMain) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.Requests);
  const [activeStep, setActiveStep] = useState(0);
  const [internetReqNum, setInternetReqNum] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const { requestsData, reqAttachSchema } = useContext(RequestsContext);
  const [uploadFilesList, setUploadFilesList] = useState<IFormMetaData>();
  const [phoneError, setPhoneError] = useState(false);
  const [isNoteError, setIsNoteError] = useState(false);
  const [isNoteTouched, setIsNoteTouched] = useState(false);
  const params = useParams();

  const handleNext = useCallback(async () => {
    const result = await files();
    if (result) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      nextStep();
    }
  }, [nextStep]);

  const initUploadFileList = useCallback(async () => {
    if (params && params.id && params.subId) {
      const getFields = await getRequestFields(
        parseInt(params.subId),
        Object.keys(reqAttachSchema).length > 0 ? reqAttachSchema : undefined
      );
      setIsPageLoading(false);

      if (getFields) {
        getFields.attachments.sort((a, b) => {
          // first, compare the isRequired property
          if (a.isRequired && !b.isRequired) {
            return -1; // a comes first if it is required and b is not
          } else if (!a.isRequired && b.isRequired) {
            return 1; // b comes first if it is required and a is not
          } else if (a.isRequired && b.isRequired) {
            // if both are required, compare the requiredFieldGroupId property
            if (
              a.requiredFieldGroupId === undefined &&
              b.requiredFieldGroupId !== undefined
            ) {
              return -1; // a comes first if its requiredFieldGroupId is undefined and b's is not
            } else if (
              a.requiredFieldGroupId !== undefined &&
              b.requiredFieldGroupId === undefined
            ) {
              return 1; // b comes first if its requiredFieldGroupId is undefined and a's is not
            } else if (
              a.requiredFieldGroupId !== undefined &&
              b.requiredFieldGroupId !== undefined
            ) {
              return a.requiredFieldGroupId - b.requiredFieldGroupId; // compare the requiredFieldGroupId values directly
            }
          }
          return 0; // if none of the above conditions are met, leave the elements in their original order
        });

        const formattedFilesList = {
          ...getFields,
          attachments: getFields.attachments.map((attachment) => ({
            ...attachment,
            requiredWith: getFields.attachments
              .filter(
                (attach) =>
                  attach.requiredFieldGroupId !== undefined &&
                  attach.requiredFieldGroupId ===
                    attachment.requiredFieldGroupId &&
                  attach.id !== attachment.id
              )
              .map((attach) => attach.id),
          })),
        } as unknown as IFormMetaData;

        setUploadFilesList(formattedFilesList);
        if (getFields.attachments.length === 0) {
          setActiveStep((prevActiveStep) => prevActiveStep + 1);
          nextStep();
        }
      }
    }
  }, [params, reqAttachSchema]);

  useEffect(() => {
    initUploadFileList();
  }, [initUploadFileList]);

  const getStep1Subtitle = () => {
    switch (params.formName) {
      default:
        return dictionary.Step1.subTitle;

      case REQUESTS_FORM_ID.updateBankDetails:
        return dictionary.Step1.bankDetailsSubtitle;
    }
  };

  const steps = [
    {
      title: dictionary.Step1.title,
      subTitle: getStep1Subtitle(),
    },
    {
      title: dictionary.Step2.title,
      subTitle: dictionary.Step2.subTitle,
    },
  ];

  const handleBack = () => {
    if (activeStep === 1 && uploadFilesList?.attachments?.length === 0) {
      window.location.reload();
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    prevStep();
  };

  const handleReset = () => {
    window.location.reload();
  };

  const dataMapper = () => {
    // Build form data object before submit
    const formData = new FormData();
    formData.append('note', requestsData.note);
    formData.append('phoneNumber', requestsData.phoneNumber);
    formData.append('phonePrefixId', requestsData.phonePrefix);
    formData.append(
      'shikumSubTypeId',
      params.subId ? params.subId.toString() : requestsData.type.toString()
    );

    if (
      requestsData?.requestDataJson &&
      Object.keys(requestsData.requestDataJson).length > 0
    ) {
      formData.append(
        'requestDataJson',
        JSON.stringify(requestsData.requestDataJson)
      );
    }

    Object.entries(requestsData.fieldAttachmentsData).forEach(
      (field, index) => {
        formData.append(
          `fieldAttachmentsData[${index}].fieldId`,
          field[1].fieldId
        );
        Object.entries(field[1].attachments).forEach((file) => {
          const renamedFile = new File(
            [file[1]],
            getCleanFileName(file[1].name),
            {
              type: file[1].type,
            }
          );

          formData.append(
            `fieldAttachmentsData[${index}].attachments`,
            renamedFile
          );
        });
      }
    );

    return formData;
  };

  useEffect(() => {
    setIsNoteError(!requestsData.note || requestsData.note === '');
  }, [isNoteTouched, requestsData.note]);

  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!isNoteTouched) setIsNoteTouched(true);

    if (isNoteError) return;

    setIsLoading(true);
    setIsPageLoading(true);
    const response = await createRequest(dataMapper());
    if (response && response.data?.internetReqNum) {
      setIsLoading(false);
      setIsPageLoading(false);
      setInternetReqNum(response.data.internetReqNum);
      setIsDialogOpen(true);
    } else {
      navigate('/error');
    }
  };

  const handleStep2Error = (value: boolean) => {
    setPhoneError(value);
  };

  const isDesktop = useIsDesktop();

  return (
    <>
      <CustomMetaTitle title={uploadFilesList?.formName || ''} />{' '}
      {isPageLoading ? (
        <Loader />
      ) : (
        <Stack gap={'40px'}>
          <CustomMetaTitle
            title={`${uploadFilesList?.formName} - ${
              activeStep === 0 ? dictionary.Step1.title : dictionary.Step2.title
            }`}
          />
          <Stack
            gap={isDesktop ? '12px' : '8px'}
            color={'text.secondary'}
            alignItems={'center'}
          >
            <Typography variant={'h3'} tabIndex={0}>
              {activeStep === 0
                ? dictionary.Step1.title
                : dictionary.Step2.title}
            </Typography>
            <Typography variant={'body3'} tabIndex={0}>
              {activeStep === 0
                ? getStep1Subtitle()
                : dictionary.Step2.subTitle}
            </Typography>
          </Stack>
          <Stack
            bgcolor={isDesktop ? '#fff' : 'transparent'}
            borderRadius={isDesktop ? '16px' : '0'}
            border={'none'}
            borderColor={'primary.shade_100'}
            boxShadow={
              isDesktop ? '-2px -2px 12px 0px rgba(8, 30, 84, 0.04)' : 'none'
            }
            padding={isDesktop ? '40px 24px 48px 24px' : '0'}
            alignItems={'center'}
          >
            <Stack
              maxWidth={
                isDesktop ? (activeStep === 1 ? '430px' : '400px') : 'initial'
              }
              gap={isDesktop ? '64px' : '32px'}
            >
              {activeStep === 0 && <Step1 formMetaData={uploadFilesList} />}
              {activeStep === 1 && (
                <Step2
                  onPhoneError={handleStep2Error}
                  isLoading={isLoading}
                  isNoteError={isNoteError}
                  isTouched={isNoteTouched}
                  setIsTouched={setIsNoteTouched}
                />
              )}
              <Stack
                direction='row'
                height={isDesktop ? '46px' : '36px'}
                justifyContent='center'
                alignItems='center'
                gap='24px'
              >
                <Button
                  variant='secondary1'
                  sx={{
                    width: isDesktop ? '180px' : '122px',
                    gap: '4px',
                    p: 0,
                    '&:disabled': { opacity: '.4', color: 'text.secondary' },
                  }}
                  disabled={
                    params.formName === REQUESTS_FORM_ID.General &&
                    activeStep === 0
                  }
                  onClick={handleBack}
                >
                  <Stack
                    direction={'row'}
                    alignItems={'flex-end'}
                    width={'auto'}
                  >
                    <DynamicArrow color={'blue'} rotate={-90} />
                    <Typography variant={'body4'}>
                      {Dictionary[DICTIONARY.General].return}
                    </Typography>
                  </Stack>
                </Button>
                <Button
                  type='submit'
                  sx={{
                    width: isDesktop ? '180px' : '122px',
                    p: 0,
                    gap: '4px',
                  }}
                  variant='primary1'
                  onClick={
                    activeStep === steps.length - 1
                      ? () => handleSubmit()
                      : handleNext
                  }
                  disabled={phoneError}
                >
                  <Stack
                    direction={'row'}
                    alignItems={'flex-end'}
                    width={'auto'}
                  >
                    <Typography variant={'body4'}>
                      {activeStep === steps.length - 1
                        ? dictionary.submit
                        : dictionary.continue}
                    </Typography>
                    <DynamicArrow color={'white'} rotate={90} />
                  </Stack>
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      )}
      <SuccessDialog
        handleClose={() => handleReset()}
        open={isDialogOpen}
        title={
          uploadFilesList?.formName
            ? dictionary.successDialog.title(uploadFilesList.formName)
            : dictionary.successDialog.altTitle
        }
        reqNum={internetReqNum}
        reqDateTime={format(new Date(), 'dd/MM/yy HH:mm')}
      />
    </>
  );
};
